import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import apiService from 'services/api';
import courseService from 'services/course';
import { CourseImportType } from 'utils/constants';

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth, selectAccount } = useAuth();
    const { message } = useSnackbar();
    const { t } = useTranslation('course');

    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [createCourseLoading, setCreateCourseLoading] = useState(false);
    const [fetchedCourses, setFetchedCourses] = useState([]);
    const [courses, setCourses] = useState(null);
    const [selectCourseDialog, setSelectCourseDialog] = useState({
        open: false,
        importType: CourseImportType.NAME,
    });
    const [showImportCourseDialog, setShowImportCourseDialog] = useState(false);
    const [formError, setFormError] = useState('');
    const [menuErrors, setMenuErrors] = useState({ courseName: '', courseId: '' });
    const [has_instructor, setHasInstructor] = useState(false);
    const [viewArchived, setViewArchived] = useState(false);

    useEffect(() => {
        setFormError('');
    }, [selectCourseDialog.open]);

    const handleImportCourse = () => {
        setShowImportCourseDialog(true);
    };

    const getAPICourses = async () => {
        setLoading(true);
        setMenuErrors((err) => ({ ...err, courseName: '' }));

        try {
            const accessToken = localStorage.getItem('access_token');
            const APICourses = await apiService.getAPICourses({
                accessToken,
                user_id: auth.user_id,
            });

            if (APICourses.errors) {
                throw new Error(APICourses.errors);
            }

            const filtered = APICourses.filter((course) => !courses.some((c) => `${c.course_id}` === `${course.id}`));

            setFetchedCourses(filtered);
            setSelectCourseDialog((prev) => ({ ...prev, open: true }));
        } catch (err) {
            console.error('Import error:', err);

            const isAuthError = err?.data?.message?.includes('401');

            if (isAuthError) {
                const errorMessage = t(
                    'course-lobby.message.fetch-course-error',
                    'There was an error fetching courses. Please launch again from LMS.'
                );
                setMenuErrors({ courseName: true, courseId: true });
                message.error(errorMessage);
            } else {
                setMenuErrors((prev) => ({
                    ...prev,
                    courseName: t('course-lobby.message.use-course-id', 'Please try using Course ID to import'),
                }));
            }
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const selectImportFromList = async () => {
        try {
            setSelectCourseDialog((prev) => ({ ...prev, importType: CourseImportType.NAME }));
            await getAPICourses();
            setShowImportCourseDialog(false);
        } catch (err) {
            console.error(err);
        }
    };

    const selectImportById = async () => {
        setSelectCourseDialog((prev) => ({ ...prev, importType: CourseImportType.ID, open: true }));
        setShowImportCourseDialog(false);
    };

    const handleSelectCard = async (accountId, course) => {
        try {
            await selectAccount(accountId, course);
        } catch (e) {
            console.log(e);
            return message.error(
                t(
                    'course-lobby.message.course-disabled',
                    'This course is disabled. Please contact IT admin for assistance.'
                )
            );
        }
    };

    const handleSelectCourse = async (selection) => {
        const isImportById = selectCourseDialog.importType === CourseImportType.ID;
        setFormError('');

        try {
            setCreateCourseLoading(true);
            const accessToken = localStorage.getItem('access_token');

            let createCourse;

            if (isImportById) {
                // TODO: add endpoint for sending course id
                // createCourse = await apiService.createAPICourse();
            } else {
                createCourse = await apiService.createAPICourse({
                    accessToken: accessToken,
                    course_data: selection,
                    user_id: auth.user_id,
                });
            }
            if (createCourse?.errors) {
                console.log('error creating course');
                throw new Error(createCourse.errors);
            }
            await selectAccount(createCourse?.account.id, createCourse.course_data);
            return history.push({
                pathname: '/assignments',
            });
        } catch (e) {
            console.log(e);

            if (!has_instructor) {
                setFormError(
                    t('course-lobby.message.permission-error', 'You do not have permission to access this platform.')
                );
            } else if (isImportById) {
                // TODO: refine conditional to catch invalid course id
                setFormError(t('course-lobby.message.invalid-course-id', 'Please enter a valid Course ID'));
            } else {
                message.error(
                    t(
                        'course-lobby.message.add-course-error',
                        'There was an error adding the course. Please try again.'
                    )
                );
            }
        } finally {
            setCreateCourseLoading(false);
        }
    };

    const handleCourseCardDropdown = ({ key, courseId }) => {
        const courseObj = courses.find((course) => course.course_id === courseId);
        switch (key) {
            case 'archive': {
                return toggleArchiveCourse(courseObj);
            }
            default:
                return;
        }
    };

    const toggleArchiveCourse = async (course) => {
        const updatedArchiveState = !course.is_archived;
        setCourses((prev) =>
            prev.map((courseItem) =>
                courseItem.course_id === course.course_id
                    ? { ...courseItem, is_archived: updatedArchiveState }
                    : courseItem
            )
        );
        await courseService.updateCourse(course.course_id, { is_archived: updatedArchiveState });
        return message.success(
            updatedArchiveState
                ? t('course-lobby.message.course-archived-success', 'Course was archived successfully')
                : t('course-lobby.message.course-unarchived-success', 'Course was unarchived successfully')
        );
    };

    const coursesInView = useMemo(() => {
        return courses?.filter((course) => {
            return viewArchived === !!course.is_archived;
        });
    }, [courses, viewArchived]);

    useEffect(() => {
        if (!auth?.user_id) return;
        (async () => {
            try {
                const { courses, accounts } = await courseService.listCourses();
                try {
                    const getAPICourses = await apiService.getAPICourses({
                        accessToken: localStorage.getItem('access_token'),
                        user_id: auth.user_id,
                    });
                    if (getAPICourses && getAPICourses.length > 0) {
                        setHasInstructor(true);
                    }
                } catch (e) {
                    console.log(e);
                }

                if (!courses || !accounts) return;
                const data = accounts.map((account) => {
                    const course = courses
                        .filter(({ active }) => active !== false) // some old course documents created from self serve admin are missing `active`
                        .find(({ id }) => id === account.course_id);
                    return {
                        course_id: course.id,
                        name: course.name,
                        cover_color: course.cover_color,
                        generated_id: course.generated_id,
                        owner_account_id: account.id,
                        role: account.role,
                        is_archived: course.is_archived,
                    };
                });
                setCourses(data);
            } catch (err) {
                console.log(err);
            }
        })();
    }, [auth?.user_id]);

    return (
        <PageContext.Provider
            value={{
                loading,
                createCourseLoading,
                courses,
                setCourses,
                getAPICourses,
                selectCourseDialog,
                setSelectCourseDialog,
                showImportCourseDialog,
                setShowImportCourseDialog,
                fetchedCourses,
                handleImportCourse,
                selectImportFromList,
                selectImportById,
                handleSelectCourse,
                handleSelectCard,
                menuErrors,
                formError,
                has_instructor,
                coursesInView,
                viewArchived,
                setViewArchived,
                handleCourseCardDropdown,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
